import type { ClientPortalSummary } from '@/lib/api/client-portal.api'
import type { PortalMetadataContext } from '@/modules/client-portal/metadata.api'
import { useCallback, useMemo } from 'react'
import {
  executeNotificationReader,
  isNotificationReaderRefused,
  useNotificationReaderRefused,
} from '@/hooks/queries/notification-reader-cache'
import { useClientPortalReadSession } from '@/hooks/queries/useClientPortalReadSession'
import {
  isPortalMetadataUnavailable,
  portalMetadataApi,
  portalMetadataKeys,
} from '@/modules/client-portal/metadata.api'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { SessionChangedError } from '@scrambl/shared/session-scope'

// Counts and branding can refresh without revoking an unchanged access proof.
const summaryOwnership = (body: ClientPortalSummary | undefined) =>
  JSON.stringify([
    body?.userId,
    body?.entity?.id,
    body?.organization?.id,
    body?.isInternalViewer,
    body?.settings,
  ])

function usePortalMetadata<T>(
  entityId: string | null | undefined,
  kind: 'summary' | 'counts',
  read: (context: PortalMetadataContext) => Promise<T>,
) {
  const session = useClientPortalReadSession(entityId)
  const client = useQueryClient()
  const key = useMemo(
    () =>
      kind === 'summary'
        ? portalMetadataKeys.summary(entityId, session.key)
        : portalMetadataKeys.counts(entityId, session.key),
    [kind, entityId, session.key],
  )
  const query = useQuery({
    queryKey: key,
    queryFn: ({ signal }) =>
      executeNotificationReader(client, key, session, signal, read),
    enabled: session.enabled,
    staleTime: kind === 'summary' ? 300000 : 60000,
    gcTime: 60000,
    retry: (failures, error) =>
      !isPortalMetadataUnavailable(error) && failures < 2,
  })
  const refused = useNotificationReaderRefused(client, key)
  const unavailable = !session.enabled || query.isError || refused
  const assertCurrent = useCallback(() => {
    session.assertCurrent()
    const current = client.getQueryCache().find({ queryKey: key, exact: true })
    if (
      unavailable ||
      !current ||
      current.state.status !== 'success' ||
      (kind === 'summary'
        ? summaryOwnership(current.state.data as ClientPortalSummary) !==
          summaryOwnership(query.data as ClientPortalSummary)
        : current.state.data !== query.data) ||
      isNotificationReaderRefused(client, key)
    )
      throw new SessionChangedError()
  }, [session, client, key, query.data, unavailable, kind])
  return {
    ...query,
    unavailable,
    isError: unavailable,
    data: unavailable ? undefined : query.data,
    assertCurrent,
    async refetch(...args: Parameters<typeof query.refetch>) {
      session.assertCurrent()
      const result = await query.refetch(...args)
      session.assertCurrent()
      return result
    },
  }
}
/** One account/entity reader shared by bootstrap, navigation, billing and Benny. */
export function useClientPortalSummary(entityId: string | null | undefined) {
  return usePortalMetadata(entityId, 'summary', portalMetadataApi.summary)
}
export function useClientPortalSidebarCounts(
  entityId: string | null | undefined,
) {
  return usePortalMetadata(entityId, 'counts', portalMetadataApi.counts)
}
